import { ArrowRight } from "lucide-react";
import { Reveal, Label } from "./Reveal";
import { CALENDLY_URL } from "../lib/contact";
import { trackEvent } from "../lib/tracking";

// Why virtual works — the national positioning section. Virtual is the core
// premium service, available anywhere in the U.S. — not a lesser option for
// families outside Denver. Primary CTA explores the virtual authority page;
// the free assessment stays one tap away as the secondary.
const POINTS = [
  "I learn your child's real patterns, routines, and schedule — before suggesting anything",
  "You get a plan built around your child, and guidance while you use it at home",
  "When a nap, a bedtime, or a night doesn't go as planned, we read it together and adjust",
  "The same one-on-one support, wherever you live — no Colorado required",
];

export const NationalVirtual = () => (
  <section id="virtual-nationwide" data-testid="national-virtual-section" className="bg-ivory">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <Label>For families nationwide</Label>
          </Reveal>
          <Reveal as="h2" delay={0.08} className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
            Why virtual sleep
            <span className="block">consulting <span className="italic text-clay">works.</span></span>
          </Reveal>
          <Reveal delay={0.14}>
            <img
              src="https://images.pexels.com/photos/18649620/pexels-photo-18649620.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="A parent and child together at home — where virtual sleep support actually happens"
              loading="lazy"
              decoding="async"
              data-testid="national-virtual-img"
              className="mt-10 w-full max-w-md rounded-[2rem] object-cover aspect-[4/3] shadow-[0_24px_50px_-25px_rgba(42,33,24,0.3)]"
            />
          </Reveal>
        </div>
      </div>

      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal>
          <div className="space-y-5 max-w-xl text-base sm:text-lg font-normal leading-relaxed text-smoke">
            <p>
              The honest question underneath it: can this actually work if
              I&rsquo;m never physically in your home? Yes — because sleep
              doesn&rsquo;t happen in my office. It happens in yours, at 2 a.m.,
              inside your real rooms and routines. That&rsquo;s exactly where
              the useful information lives — and where our work happens too.
            </p>
            <p>
              And virtual support isn&rsquo;t a phone call and a generic plan.
              I learn your child&rsquo;s actual sleep and your family&rsquo;s
              real schedule first — then we build recommendations around your
              child, and I stay with you while you put them into practice.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <ul className="mt-10 max-w-xl border-y border-ink/10 divide-y divide-ink/10">
            {POINTS.map((p, i) => (
              <li key={p} data-testid={`national-point-${i}`} className="py-4 flex items-baseline gap-4 text-base sm:text-lg font-normal text-smoke">
                <span aria-hidden="true" className="h-px w-5 shrink-0 bg-clay translate-y-[-5px]" />
                {p}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.14}>
          <p data-testid="national-virtual-line" className="mt-10 max-w-xl font-serif italic font-light text-2xl sm:text-3xl text-clay leading-snug">
            The most important part of sleep support doesn&rsquo;t happen in my
            office. It happens in your home — in the moments when you&rsquo;re
            actually putting the plan into practice.
          </p>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-10 flex flex-col items-start gap-5">
            <a
              data-testid="national-virtual-link"
              href="/virtual-sleep-consultant"
              onClick={() => {
                trackEvent("cta_click", { location: "national_virtual" });
                trackEvent("virtual_service_click", { location: "national_virtual" });
              }}
              className="w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300 group"
            >
              Explore Virtual Sleep Consulting
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              data-testid="national-virtual-cta"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("cta_click", { location: "national_virtual_book" });
                trackEvent("book_assessment_click", { location: "national_virtual" });
              }}
              className="inline-flex items-center gap-1.5 text-sm font-normal text-ink underline underline-offset-8 decoration-ink/20 hover:decoration-clay hover:text-claydark transition-[text-decoration-color,color] duration-300"
            >
              Book my complimentary sleep assessment
              <ArrowRight size={13} />
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
